// This is a render prop function that you can use anywhere to get a countdown timer.
// Timers with the same id will receive the same data to allow multiple count down timers to stay
// in sync.  Therefore, the id should be unique to the time interval being tracked.
// (e.g. countdown timers for the duration of a timed quiz on different pages will all use the same
// id because they are all tracking the same quiz duration)
// The local expiresAt makes sure we do not depend on the backend timestamp, because the local
// timestamp may be different.  Instead, we calculate and store a local timestamp based on the time left.
import type { ComponentType, ReactNode } from 'react';
// eslint-disable-next-line import/no-duplicates
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
// eslint-disable-next-line import/no-duplicates
import * as React from 'react';
import { createContext } from 'react';

type LocalExpiresAtRenderProps = {
  expiresAt?: number | null;
};

type LocalExpiresAtContextValue = {
  getLocalTimer: (id: string) => number | undefined;
  updateLocalTimer: (id: string, expiresAt: number | undefined) => void;
};

const LocalExpiresAtContext = createContext<LocalExpiresAtContextValue>({
  getLocalTimer: () => undefined,
  updateLocalTimer: () => undefined,
});

export const LocalExpiresAtContextProvider = ({ children }: { children?: ReactNode }) => {
  const [localTimers, setLocalTimers] = useState(() => new Map<string, number | undefined>());

  const getLocalTimer = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (id: string) => localTimers.get(id),
    [localTimers]
  );

  const updateLocalTimer = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (id: string, expiresAt: number | undefined) => {
      setLocalTimers((prevState) => {
        const nextState = new Map(prevState);
        nextState.set(id, expiresAt);
        return nextState;
      });
    },
    [setLocalTimers]
  );

  const contextValue = useMemo<LocalExpiresAtContextValue>(
    () => ({ getLocalTimer, updateLocalTimer }),
    [getLocalTimer, updateLocalTimer]
  );

  return <LocalExpiresAtContext.Provider value={contextValue}>{children}</LocalExpiresAtContext.Provider>;
};

export const withLocalExpiresAtContextProvider = <BaseProps extends {}>(BaseComponent: ComponentType<BaseProps>) => {
  return (props: BaseProps) => (
    <LocalExpiresAtContextProvider>
      <BaseComponent {...props} />
    </LocalExpiresAtContextProvider>
  );
};

const LocalExpiresAt = ({
  id,
  remainingTimeInMs,
  children,
}: {
  id: string;
  // This is sent in from the BE and does not get updated until a new BE request is made. The timer counts down from this time.
  remainingTimeInMs?: number;
  children: (x0: LocalExpiresAtRenderProps) => React.ReactElement | null;
}) => {
  const { getLocalTimer, updateLocalTimer } = useContext(LocalExpiresAtContext);
  const expiresAt = getLocalTimer(id);

  useEffect(() => {
    if (typeof remainingTimeInMs === 'number') {
      updateLocalTimer(id, Date.now() + remainingTimeInMs);
    }
  }, [id, remainingTimeInMs, updateLocalTimer]);

  return children({ expiresAt });
};

export default LocalExpiresAt;
